import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './PDTable'
import { Input, Select, assets } from '@pulseops/common'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import { ActivityIndicator, Text, View } from 'react-native'
import {
  AuthorizationRow,
  AuthorizedPerson,
  PolicyDispatchingService,
  RdoItem,
  RdoSearchResponseHasValue,
  StockOutAgentCancelPayload,
  StockOutAgentChangeDatePayload,
  StockOutAgentDeliveryPayload,
  StockOutAgentPolicy,
  StockOutAgentSearchBody,
  StockOutAgentSearchResponse
} from '../services/PolicyDispatchingService'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import PolicyDispatchingDialog from './PolicyDispatchingDialog'
import PDChangeInfoDialog from './PDChangeInfoDialog'
import { borderedInputStyle, borderedSelectStyle } from '../styles/policyDispatchingStyles'
import { formatDate, getStatusLabel, getStatusStyle, getStatusValue } from '../utils/policyDispatchingUtils'
import ChoosePersonDialog from './ChoosePersonDialog'

const headCell = [
  { id: 1, label: 'Số hợp đồng' },
  { id: 2, label: 'Ngày phát hành đầu tiên' },
  { id: 3, label: 'Ngày phát hành' },
  { id: 4, label: 'Văn phòng' },
  { id: 5, label: 'Tình trạng' },
  { id: 6, label: 'Ngày giao hợp đồng' },
  { id: 7, label: 'Người nhận' },
  { id: 8, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'contractNumber' },
  { id: 2, label: 'firstIssueDate' },
  { id: 3, label: 'issueDate' },
  { id: 4, label: 'branch' },
  { id: 5, label: 'status' },
  { id: 6, label: 'deliveryDate' },
  { id: 7, label: 'receiver' },
  { id: 8, label: 'note' }
]

const headAuthorizedCell = [
  { id: 1, label: 'Số ủy quyền' },
  { id: 2, label: 'Người ủy quyền' },
  { id: 3, label: 'Người được ủy quyền' },
  { id: 4, label: 'Ngày có hiệu lực' },
  { id: 5, label: 'Hạn dùng' },
  { id: 6, label: 'Xác nhận bởi' }
]

const rowAuthorizedCell = [
  { id: 1, label: 'id' },
  { id: 2, label: 'authorizationAgent' },
  { id: 3, label: 'authorizedPerson' },
  { id: 4, label: 'effectiveDate' },
  { id: 5, label: 'expiryDate' },
  { id: 6, label: 'approver' }
]

const headAreaManagerCell = [
  { id: 1, label: 'Mã RDO' },
  { id: 2, label: 'Người quản lý khu vực' },
  { id: 3, label: 'Văn phòng' },
  { id: 4, label: 'Ngày tham gia' },
  { id: 5, label: 'Loại' }
]

const rowAreaManagerCell = [
  { id: 1, label: 'id' },
  { id: 2, label: 'fullName' },
  { id: 3, label: 'office' },
  { id: 4, label: 'joinDate' },
  { id: 5, label: 'type' }
]

type StockOutAgentFormValues = {
  agentCode: string
  agentName: string
  type: string
  dateAppointed: string
  dateTerminated: string
  status: string
  salesUnitCode: string
  area: string
  gaCode: string
  salesBranch: string
  zone: string
  branch: string
  receiverOption: any
  receiver: any
  note: string
  policyStatus: any
}

const statusOptions = [
  { value: 'AVAILABLE', label: 'Còn trong kho' },
  { value: 'IN_USED', label: 'Đã giao' },
  { value: 'RETURNED', label: 'Đã trả lại' }
]

const receiverOptions = [
  { value: 'agent', label: 'Đại lý' },
  { value: 'authorizedPerson', label: 'Người được ủy quyền' },
  { value: 'areaManager', label: 'Quản lý khu vực' },
  { value: 'other', label: 'Khác' }
]

const getStockOutAgentSearchBody = (response: StockOutAgentSearchResponse): StockOutAgentSearchBody => {
  return 'body' in response ? response.body : response
}

const hasRDOBody = (response: unknown): response is RdoSearchResponseHasValue => {
  return Boolean(
    response &&
      typeof response === 'object' &&
      'body' in response &&
      (response as RdoSearchResponseHasValue).body &&
      Array.isArray((response as RdoSearchResponseHasValue).body.items) &&
      (response as RdoSearchResponseHasValue).body.page
  )
}

const getRDOResponse = (response: unknown): RdoSearchResponseHasValue | null => {
  const data =
    response && typeof response === 'object' && '_tag' in response && (response as any)._tag === 'Right'
      ? (response as any).right
      : response

  return hasRDOBody(data) ? data : null
}

const hasAuthorizePersonBody = (response: unknown): response is { body: { items: AuthorizedPerson[] } } =>
  Boolean(response) && typeof response === 'object' && 'body' in (response as object)

export type PDStockOutAgentHandle = {
  handleDeliveryContracts: () => void
  handleCancelContracts: () => void
  handleAdjustDeliveryDate: () => void
}

function PDStockOutAgent({ styles }: { styles: { [key: string]: any } }, ref: React.Ref<PDStockOutAgentHandle>) {
  const { officeCode } = usePolicyDispatchingOffice()
  const [rows, setRows] = React.useState<StockOutAgentPolicy[]>([])
  const [selectedRows, setSelectedRows] = React.useState<StockOutAgentPolicy[]>([])
  const [allRowsSelected, setAllRowsSelected] = React.useState(false)
  const [pageInfo, setPageInfo] = React.useState<{
    page: number
    totalPages: number
    totalElements: number
    first: boolean
    last: boolean
  } | null>(null)
  const lastSearchValues = React.useRef<StockOutAgentFormValues | null>(null)
  const [openChangeDeliveryDateDialog, setOpenChangeDeliveryDateDialog] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [openModal, setOpenModal] = React.useState(false)
  const [modalTitle, setModalTitle] = React.useState('Thông báo')
  const [modalMessage, setModalMessage] = React.useState('')
  const [openChoosePersonDialog, setOpenChoosePersonDialog] = React.useState(false)
  const [choosePersonRows, setChoosePersonRows] = React.useState<AuthorizationRow[] | RdoItem[]>([])
  const [choosePersonLoading, setChoosePersonLoading] = React.useState(false)
  const [headCells, setHeadCells] = React.useState<any[]>([])
  const [rowCells, setRowCells] = React.useState<any[]>([])
  const [additionalData, setAdditionalData] = React.useState({ title: '', key: '' })
  const { control, handleSubmit, setValue, watch, getValues } = useForm<StockOutAgentFormValues>({
    defaultValues: {
      agentCode: '',
      agentName: '',
      type: '',
      dateAppointed: '',
      dateTerminated: '',
      status: '',
      salesUnitCode: '',
      area: '',
      gaCode: '',
      salesBranch: '',
      zone: '',
      branch: '',
      receiverOption: null,
      receiver: '',
      note: '',
      policyStatus: statusOptions[0]
    }
  })

  const search = (values: StockOutAgentFormValues, page: number, resetSelection = false) => {
    if (!officeCode) {
      setOpenModal(true)
      setModalTitle('Văn phòng')
      setModalMessage('Vui lòng chọn văn phòng trước khi tìm kiếm.')
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchStockOutAgent({
        agentCode: values.agentCode,
        officeCode,
        status: values.policyStatus?.value || null,
        page
      }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          const body = getStockOutAgentSearchBody(response)
          setValue('agentName', body.agent.agentName || '')
          setValue('type', body.agent.type || '')
          setValue('dateAppointed', body.agent.dateAppointed || '')
          setValue('dateTerminated', body.agent.dateTerminated || '')
          setValue('status', body.agent.status || '')
          setValue('salesUnitCode', body.agent.salesUnitCode || '')
          setValue('area', body.agent.area || '')
          setValue('gaCode', body.agent.gaCode || '')
          setValue('salesBranch', body.agent.salesBranch || '')
          setValue('zone', body.agent.zone || '')
          setValue('branch', body.agent.branch || '')

          const preserveAllSelected = !resetSelection && allRowsSelected
          const selectedKeys = new Set((resetSelection ? [] : selectedRows).map((row) => row.contractNumber))
          if (resetSelection) {
            setSelectedRows([])
            setAllRowsSelected(false)
          }
          setRows(
            (body.policies || []).map((item) => ({
              ...item,
              status: getStatusLabel(item.status),
              checked: preserveAllSelected || selectedKeys.has(item.contractNumber)
            }))
          )
          setPageInfo({
            page: body.page,
            totalPages: body.totalPages,
            totalElements: body.totalElements,
            first: body.page === 0,
            last: body.page >= body.totalPages - 1
          })
          setIsLoading(false)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search stock-out agent', error)
          setRows([])
          setSelectedRows([])
          setAllRowsSelected(false)
          setPageInfo(null)
          setIsLoading(false)
          setOpenModal(true)
          setModalTitle('Tìm')
          setModalMessage('Không thể tìm thông tin đại lý. Vui lòng thử lại.')
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => {
    lastSearchValues.current = values
    search(values, 0, true)
  })

  const handlePageChange = (page: number) => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, page)
    }
  }

  const refreshSearch = () => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, 0, true)
    }
  }

  const handleDeliveryContracts = () => {
    if (selectedRows.length === 0) {
      setOpenModal(true)
      setModalTitle('Giao hợp đồng')
      setModalMessage('Vui lòng chọn ít nhất một hợp đồng để giao.')
      return
    }

    const agentCode = watch('agentCode')
    const receiver = watch('receiver')
    const note = watch('note')
    if (!receiver) {
      setOpenModal(true)
      setModalTitle('Giao hợp đồng')
      setModalMessage('Vui lòng nhập tên người nhận trước khi giao.')
      return
    }

    const condition = selectedRows.every((row) => getStatusValue(row.status) === 'AVAILABLE')
    if (!condition) {
      setOpenModal(true)
      setModalTitle('Giao hợp đồng')
      setModalMessage('Chỉ có thể giao các hợp đồng đang ở trạng thái "Còn trong kho". Vui lòng kiểm tra lại.')
      return
    }

    const payload: StockOutAgentDeliveryPayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      agentCode,
      receiver,
      remark: note || row.note || ''
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.deliverStockOutAgent(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setSelectedRows([])
          setIsLoading(false)
          setOpenModal(true)
          setModalTitle('Giao hợp đồng')
          setModalMessage('Giao hợp đồng thành công')
          refreshSearch()
          setValue('receiverOption', null)
          setValue('receiver', '')
          setValue('note', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setOpenModal(true)
          setModalTitle('Giao hợp đồng')
          setModalMessage('Không thể giao hợp đồng. Vui lòng thử lại sau.')
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleCancelContracts = () => {
    if (selectedRows.length === 0) {
      setOpenModal(true)
      setModalTitle('Hủy giao hợp đồng')
      setModalMessage('Vui lòng chọn ít nhất một hợp đồng để hủy giao.')
      return
    }

    const agentCode = watch('agentCode')
    const condition = selectedRows.every((row) => getStatusValue(row.status) === 'IN_USED')
    if (!condition) {
      setOpenModal(true)
      setModalTitle('Hủy giao hợp đồng')
      setModalMessage('Chỉ có thể hủy giao các hợp đồng đang ở trạng thái "Đã giao". Vui lòng kiểm tra lại.')
      return
    }

    const isValid = selectedRows.every((item) => !item?.letterEntryDate)
    if (!isValid) {
      setOpenModal(true)
      setModalTitle('Hủy giao hợp đồng')
      setModalMessage('Chỉ có thể hủy giao các hợp đồng chưa có ngày nhập thư. Vui lòng kiểm tra lại.')
      return
    }

    const payload: StockOutAgentCancelPayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      agentCode
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.cancelStockOutAgent(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setSelectedRows([])
          setIsLoading(false)
          setModalTitle('Hủy giao hợp đồng')
          setModalMessage('Hủy giao hợp đồng thành công')
          setOpenModal(true)
          refreshSearch()
          setValue('receiverOption', null)
          setValue('receiver', '')
          setValue('note', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setOpenModal(true)
          setModalTitle('Hủy giao hợp đồng')
          setModalMessage('Không thể hủy giao hợp đồng. Vui lòng thử lại sau.')
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleAdjustDeliveryDate = () => {
    if (selectedRows.length === 0) {
      setOpenModal(true)
      setModalTitle('Điều chỉnh ngày giao')
      setModalMessage('Vui lòng chọn ít nhất một hợp đồng để điều chỉnh ngày giao.')
      return
    }

    const condition = selectedRows.every((row) => getStatusValue(row.status) === 'IN_USED')
    if (!condition) {
      setOpenModal(true)
      setModalTitle('Điều chỉnh ngày giao')
      setModalMessage(
        'Chỉ có thể điều chỉnh ngày giao cho các hợp đồng đang ở trạng thái "Đã giao". Vui lòng kiểm tra lại.'
      )
      return
    }

    setOpenChangeDeliveryDateDialog(true)
  }

  const handleChangeDeliveryDate = (newDeliveryDate: Date, remark: string) => {
    const agentCode = watch('agentCode')
    const payload: StockOutAgentChangeDatePayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      agentCode,
      newDeliveryDate: formatDate(newDeliveryDate) || '',
      remark
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.changeDateStockOutAgent(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setSelectedRows([])
          setIsLoading(false)
          setOpenModal(true)
          setModalTitle('Điều chỉnh ngày giao')
          setModalMessage('Điều chỉnh ngày giao thành công')
          refreshSearch()
          setValue('receiverOption', null)
          setValue('receiver', '')
          setValue('note', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setOpenModal(true)
          setModalTitle('Điều chỉnh ngày giao')
          setModalMessage('Không thể điều chỉnh ngày giao. Vui lòng thử lại sau.')
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  React.useImperativeHandle(ref, () => ({ handleDeliveryContracts, handleCancelContracts, handleAdjustDeliveryDate }))

  const handleOpenAuthorizationDialog = () => {
    const agentCode = watch('agentCode')
    if (!agentCode) {
      setOpenModal(true)
      setModalTitle('Mã số đại lý')
      setModalMessage('Vui lòng nhập mã số đại lý trước khi tìm kiếm.')
      return
    }
    setChoosePersonLoading(true)
    pipe(
      PolicyDispatchingService.getAuthorizedPersonsByAgent(agentCode),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!hasAuthorizePersonBody(response)) {
            setChoosePersonRows([])
            setChoosePersonLoading(false)
            setOpenModal(true)
            setModalTitle('Danh sách ủy quyền')
            setModalMessage('Đại lý này hiện không có danh sách nguời ủy quyền.')
            return
          }

          setOpenChoosePersonDialog(true)
          setAdditionalData({ title: 'Danh sách giấy ủy quyền', key: 'authorizedPerson' })
          setHeadCells(headAuthorizedCell)
          setRowCells(rowAuthorizedCell)
          setChoosePersonRows(
            response.body.items.map((item) => ({
              id: item.authorizationLetterId,
              authorizationAgent: item.agentNumber,
              authorizedPerson: item.authorizedPerson,
              effectiveDate: item.effectiveFromDate,
              expiryDate: item.effectiveToDate,
              approver: item.approver
            }))
          )
          setChoosePersonLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setChoosePersonRows([])
          setChoosePersonLoading(false)
          setOpenModal(true)
          setModalTitle('Danh sách ủy quyền')
          setModalMessage('Không thể tải danh sách người ủy quyền. Vui lòng thử lại sau.')
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleOpenAreaManager = async () => {
    setIsLoading(true)
    try {
      const firstResponse = await pipe(PolicyDispatchingService.searchRdo({ nonFileNet: 'true' }), ZIO.runPromise({}))
      const firstSearchResult = getRDOResponse(firstResponse)
      if (!firstSearchResult || firstSearchResult.body.items.length === 0) {
        setChoosePersonRows([])
        setIsLoading(false)
        setOpenModal(true)
        setModalTitle('Danh sách quản lý khu vực')
        setModalMessage('Không tìm thấy kết quả quản lý khu vực.')
        return
      }

      const remainingResponses = await Promise.all(
        Array.from({ length: Math.max(firstSearchResult.body.page.totalPages - 1, 0) }, (_, index) =>
          pipe(PolicyDispatchingService.searchRdo({ page: index + 1, nonFileNet: 'true' }), ZIO.runPromise({}))
        )
      )
      const items = [firstSearchResult, ...remainingResponses].flatMap((response) => {
        const searchResponse = getRDOResponse(response)
        return searchResponse ? searchResponse.body.items : []
      })

      if (items.length === 0) {
        setOpenModal(true)
        setModalTitle('Danh sách quản lý khu vực')
        setModalMessage('Không tìm thấy kết quả quản lý khu vực.')
        return
      }

      setHeadCells(headAreaManagerCell)
      setRowCells(rowAreaManagerCell)
      setAdditionalData({ title: 'Danh sách người quản lý khu vực', key: 'fullName' })
      setChoosePersonRows(items.map((item) => ({ ...item, id: item.rdoCode })))
      setOpenChoosePersonDialog(true)
      setIsLoading(false)
    } catch {
      setChoosePersonRows([])
      setIsLoading(false)
      setOpenModal(true)
      setModalTitle('Danh sách quản lý khu vực')
      setModalMessage('Không thể tải danh sách quản lý khu vực. Vui lòng thử lại sau.')
    }
  }

  const handleReceiverOptionChange = (
    option: StockOutAgentFormValues['receiverOption'],
    onChange: (value: StockOutAgentFormValues['receiverOption']) => void
  ) => {
    onChange(option)

    const receiverByOption = {
      agent: watch('agentName'),
      authorizedPerson: '',
      areaManager: '',
      other: ''
    }

    setValue('receiver', option ? receiverByOption[option.value as keyof typeof receiverByOption] : '')
  }

  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 24, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalTitle}
        message={modalMessage}
        onClose={() => setOpenModal(false)}
      />
      <View
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 24, ...styles.column_1_4 } as any}
      >
        <Controller
          name="agentCode"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              title={'Mã số đại lý'}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              errorMessage={error?.message}
              containerStyle={{ flex: 1 }}
              inputStyle={borderedInputStyle}
              filled
              required
              maxLength={8}
            />
          )}
        />
        <button style={styles.submitButton} type="button" onClick={handleSearch}>
          <span style={styles.mr_5}>
            <assets.SearchWhite />
          </span>
          Tìm
        </button>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="agentName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên đại lý'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Loại'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="dateAppointed"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ngày gia nhập'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="dateTerminated"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ngày kết thúc'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tình trạng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="salesUnitCode"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Nhóm'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="area"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Khu vực'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="gaCode"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'GA/BP'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="salesBranch"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ban'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="zone"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Vùng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="branch"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Văn phòng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                filled
                inputStyle={borderedInputStyle}
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <Text style={styles.subTitle}> - Thông tin người nhận</Text>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              flex: 1,
              minWidth: 0,
              marginTop: 4
            } as any
          }
        />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="receiverOption"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Người nhận'}
                options={receiverOptions}
                placeholder={'Chọn người nhận'}
                onChange={(e) => {
                  handleReceiverOptionChange(e, onChange)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
                required
              />
            )}
          />
        </View>

        <View style={styles.column_1_4}>
          <Controller
            name="receiver"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên người nhận'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled={watch('receiverOption')?.value !== 'other'}
                alwayShowUnderline
                required
              />
            )}
          />
        </View>
      </View>

      {watch('receiverOption')?.value === 'authorizedPerson' && (
        <button
          type="button"
          onClick={handleOpenAuthorizationDialog}
          style={
            {
              border: 0,
              background: 'none',
              color: '#ed1b2e',
              textDecoration: 'underline',
              padding: 0,
              width: 'fit-content',
              fontSize: 16,
              marginTop: '-16px',
              fontWeight: '500'
            } as any
          }
        >
          Danh sách ủy quyền
        </button>
      )}

      {watch('receiverOption')?.value === 'areaManager' && (
        <button
          type="button"
          onClick={handleOpenAreaManager}
          style={
            {
              border: 0,
              background: 'none',
              color: '#ed1b2e',
              textDecoration: 'underline',
              padding: 0,
              width: 'fit-content',
              fontSize: 16,
              marginTop: '-16px',
              fontWeight: '500'
            } as any
          }
        >
          Danh sách quản lý khu vực
        </button>
      )}

      <View style={{ width: '50%' } as any}>
        <Controller
          name="note"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              title={'Ghi chú'}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              errorMessage={error?.message}
              containerStyle={{ flex: 1 }}
              multiline
              numberOfLines={5}
              inputStyle={{ padding: 10, marginTop: 4 }}
              filled
            />
          )}
        />
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <Text style={styles.subTitle}> - Danh sách hợp đồng</Text>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              flex: 1,
              minWidth: 0,
              marginTop: 4
            } as any
          }
        />
      </View>

      <View style={{ width: '15%' } as any}>
        <Controller
          name="policyStatus"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Select
              label={'Tình trạng'}
              options={statusOptions}
              placeholder={'Chọn tình trạng'}
              menuPlacement="below"
              onChange={(e) => {
                onChange(e)
                const searchValues = { ...getValues(), policyStatus: e }
                lastSearchValues.current = searchValues
                search(searchValues, 0, true)
              }}
              onBlur={onBlur}
              value={value}
              key={value?.value}
              errorMessage={error?.message}
              containerStyles={borderedSelectStyle}
              filled
            />
          )}
        />
      </View>

      <View>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          hasCheckbox
          hasCheckAll
          getStyle={(cell, value) => (cell === 'status' ? getStatusStyle(value) : {})}
          onSelectionChange={(nextRows, nextSelectedRows) => {
            setRows(nextRows)
            setSelectedRows((currentSelectedRows) => {
              const currentPageKeys = new Set(rows.map((row) => row.contractNumber))
              const selectionsFromOtherPages = currentSelectedRows.filter(
                (row) => !currentPageKeys.has(row.contractNumber)
              )
              const mergedSelectedRows = [...selectionsFromOtherPages, ...nextSelectedRows]

              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && mergedSelectedRows.length === pageInfo.totalElements)
              )

              return mergedSelectedRows
            })
          }}
          onSelectAllChange={(selected) => {
            setAllRowsSelected(selected)
            setRows((currentRows) => currentRows.map((row) => ({ ...row, checked: selected })))

            if (!selected) {
              setSelectedRows([])
              return
            }

            if (!officeCode || !lastSearchValues.current || !pageInfo || pageInfo.totalPages <= 1) {
              setSelectedRows(rows)
              return
            }

            const values = lastSearchValues.current
            setIsLoading(true)
            Promise.all(
              Array.from({ length: pageInfo.totalPages }, (_, page) =>
                pipe(
                  PolicyDispatchingService.searchStockOutAgent({
                    agentCode: values.agentCode,
                    officeCode,
                    status: values.policyStatus?.value || null,
                    page
                  }),
                  ZIO.runPromise({})
                )
              )
            )
              .then((responses) => {
                const allPolicies = responses.flatMap((response) => {
                  const data =
                    response && typeof response === 'object' && '_tag' in response
                      ? (response as any)._tag === 'Right'
                        ? (response as any).right
                        : null
                      : response
                  return data ? getStockOutAgentSearchBody(data as StockOutAgentSearchResponse).policies || [] : []
                })
                setSelectedRows(allPolicies.length > 0 ? allPolicies : rows)
                setIsLoading(false)
              })
              .catch(() => {
                setSelectedRows(rows)
                setIsLoading(false)
                setOpenModal(true)
                setModalTitle('Chọn tất cả')
                setModalMessage('Không thể tải toàn bộ danh sách hợp đồng. Đã chọn các hợp đồng ở trang hiện tại.')
              })
          }}
          allRowsSelected={Boolean(allRowsSelected && pageInfo && pageInfo.totalElements > 0)}
          pagination={
            pageInfo
              ? {
                  ...pageInfo,
                  onPageChange: handlePageChange
                }
              : undefined
          }
        />
      </View>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}

      <ChoosePersonDialog
        open={openChoosePersonDialog}
        loading={choosePersonLoading}
        title={additionalData.title}
        rows={choosePersonRows}
        headCell={headCells}
        rowCell={rowCells}
        keySubmit={additionalData.key}
        nameFilterField={additionalData.key === 'fullName' ? 'fullName' : undefined}
        onClose={() => setOpenChoosePersonDialog(false)}
        onSelect={(authorizedPerson) => {
          setValue('receiver', authorizedPerson)
          setOpenChoosePersonDialog(false)
        }}
      />

      <PDChangeInfoDialog
        visible={openChangeDeliveryDateDialog}
        selectedCount={selectedRows.length}
        currentDates={selectedRows.map((row) => row.deliveryDate)}
        stockInDates={selectedRows.map((row) => row.stockInDate)}
        action="changeDate"
        title="Điều chỉnh ngày giao"
        dateLabel="Ngày giao mới"
        duplicateDateMessage="Ngày giao mới không được trùng với ngày giao hiện tại."
        onClose={() => setOpenChangeDeliveryDateDialog(false)}
        onConfirm={(value, remark) => {
          handleChangeDeliveryDate(value as Date, remark)
          setOpenChangeDeliveryDateDialog(false)
        }}
      />
    </View>
  )
}

export default React.forwardRef(PDStockOutAgent)
